import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { GripVertical, Plus, Save, X, Eye, EyeOff, ArrowLeft, Calendar, ImageOff, Edit, CheckSquare } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../../../axiosConfig'; // Ensure this is correctly configured

// ============================================================================
// SINGLE ADD POPUP - Local Add Only + Thumbnail Preview
// ============================================================================
const DestinationsPopup = ({ selectedItem, onClose, onAddToCarousel, isMulti = false, selectedItems = [] }) => {
  
  const handleAddToCarousel = () => {
    const itemsToAdd = isMulti
      ? selectedItems.map(item => ({
          selectedItem: item
        }))
      : [{
          selectedItem
        }];

    onAddToCarousel(itemsToAdd);
    onClose();
  };

  const title = isMulti
    ? `Add ${selectedItems.length} Destinations`
    : `Add: ${selectedItem?.name || 'Item'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 px-6 py-6 space-y-6 overflow-y-auto">
          {isMulti ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Selected destinations:</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {selectedItems.map((item) => (
                  <div key={item.id} className="p-3 border border-gray-200 rounded bg-gray-50">
                    <p className="text-sm font-medium truncate">{item.name}</p>
                    <p className="text-xs text-gray-500">{item.city || 'Unknown'}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : selectedItem && (
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="relative w-32 h-32 overflow-hidden bg-gray-100 border-2 border-gray-300 border-dashed rounded-lg">
                    {selectedItem?.imageUrl ? (
                      <img src={selectedItem.imageUrl} alt={selectedItem.name} className="object-cover w-full h-full" />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <ImageOff size={32} />
                        <span className="mt-2 text-xs">No Image</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1">
                  <h3 className="mb-1 text-lg font-semibold text-gray-800">{selectedItem.name}</h3>
                  <p className="text-sm text-gray-600">{selectedItem.city || 'Unknown City'}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button onClick={onClose} className="px-6 py-2 font-medium text-gray-700 transition-colors border border-gray-300 rounded-lg hover:bg-gray-100">
            Cancel
          </button>

          <button
            onClick={handleAddToCarousel}
            className="flex items-center gap-2 px-6 py-2 font-medium text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add to Carousel
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CONTEXT & STATE
// ============================================================================
const CMSContext = createContext();
const useCMS = () => {
  const context = useContext(CMSContext);
  if (!context) throw new Error('useCMS must be used within CMSProvider');
  return context;
};

// ============================================================================
// INITIAL SECTIONS - Removed page-body-items section
// ============================================================================
const initialSections = [
  {
    id: 'carousel-items',
    title: 'Selected Carousel Items',
    order: 1,
    visible: true,
    type: 'multiple',
    items: []
  }
];

const getFirstImageUrl = (images, baseUrl = '') => {
  if (!Array.isArray(images) || images.length === 0) return null;
  const sorted = [...images].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  const first = sorted[0] || {};
  if (first.webImageUrl) return first.webImageUrl;
  if (first.image?.path) return `${baseUrl}${first.image.path}`;
  if (first.path) return `${baseUrl}${first.path}`;
  return null;
};

// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================
const Button = ({ children, onClick, variant = 'primary', size = 'md', className = '', icon: Icon, disabled, ...props }) => {
  const baseStyles = 'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200';
  const variants = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    ghost: 'hover:bg-gray-100 text-gray-600',
    success: 'bg-green-600 hover:bg-green-700 text-white'
  };
  const sizes = { sm: 'px-3 py-1.5 text-sm', md: 'px-4 py-2 text-sm', lg: 'px-6 py-3 text-base' };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      {...props}
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
};

const Card = ({ children, className = '', onClick }) => {
  return (
    <div onClick={onClick} className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
      {children}
    </div>
  );
};

// ============================================================================
// BLOCK CARD - close button always visible
// ============================================================================
const ItemsBlockCard = ({ section, titleOverride, allowEdit = false, validityLabel = '' }) => {
  const { items, title, visible } = section;
  const visibleItems = (items || []).filter((it) => it.isActive !== false);

  const { removeFromBlock, updateSection, toggleSectionVisibility } = useCMS();

  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState(title || '');
  const [editItemsPerBlock, setEditItemsPerBlock] = useState(section.itemsPerBlock || (items ? items.length : 0));

  const handleRemove = (itemId) => {
    removeFromBlock(section.id, itemId);
  };

  const openEdit = () => {
    setEditTitle(title || '');
    setEditItemsPerBlock(section.itemsPerBlock || (items ? items.length : 0));
    setEditOpen(true);
  };

  const saveEdit = () => {
    const updated = { ...section, title: editTitle, itemsPerBlock: Number(editItemsPerBlock) };
    updateSection(updated);
    setEditOpen(false);
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{titleOverride || title}</h3>
          <p className="text-sm text-gray-500">{visibleItems.length} item(s)</p>
        </div>
        <div className="flex items-center gap-2">
          {/* {allowEdit && (
            <button onClick={openEdit} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm text-gray-700">
              <Edit size={16} /> Edit
            </button>
          )} */}
          {/* <button
            onClick={() => toggleSectionVisibility(section.id)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm text-gray-700"
          >
            {visible ? <><Eye size={16} /> Visible</> : <><EyeOff size={16} /> Hidden</>}
          </button> */}
        </div>
      </div>
      {visibleItems.length === 0 ?  (
        <div className="py-12 text-center text-gray-400">
          <p>No items added yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {visibleItems.map((item) => {
            const attraction = item.selectedItem;
            const imageUrl = attraction.imageUrl || null;

            return (
              <div key={item.id} className="relative overflow-hidden transition-all duration-200 bg-white border border-gray-200 rounded-lg shadow-sm group hover:shadow-md hover:-translate-y-1">
                {/* Small close button to remove from carousel - always visible */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(item.id);
                  }}
                  className="absolute top-2 right-2 z-20 bg-white/90 hover:bg-red-50 text-gray-600 hover:text-red-600 rounded-full p-1.5 shadow-sm transition-all duration-200"
                  title="Remove from carousel"
                >
                  <X size={14} />
                </button>

                <div className="overflow-hidden bg-gray-100 aspect-square">
                  {imageUrl ? (
                    <img src={imageUrl} alt={attraction.name} className="object-cover w-full h-full" />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full text-xs text-gray-400 bg-gray-200 border-2 border-dashed rounded-lg">
                      No Image
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h4 className="text-sm font-semibold text-center text-gray-900 truncate">{attraction.name}</h4>
                  {/* <p className="mt-1 text-xs text-center text-gray-500">
                    {validityLabel || 'Inherits carousel validity'}
                  </p> */}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-md overflow-hidden bg-white rounded-lg shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Edit Carousel Block</h2>
              <button onClick={() => setEditOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Block Title</label>
                <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Items Per Block</label>
                <input type="number" min={1} value={editItemsPerBlock} onChange={(e) => setEditItemsPerBlock(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button onClick={() => setEditOpen(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg">Cancel</button>
              <button onClick={saveEdit} className="px-4 py-2 text-white bg-blue-600 rounded-lg">Save</button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

const SectionCard = ({ section, onEdit, onToggleVisibility, onImageClick, dragHandle }) => {
  const { items, title, visible } = section;
  return (
    <Card className={`p-6 ${!visible ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div {...dragHandle} className="text-gray-400 cursor-grab active:cursor-grabbing hover:text-gray-600">
            <GripVertical size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{items.length} item(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={onToggleVisibility} variant="ghost" size="sm" icon={visible ? Eye : EyeOff}>
            {visible ? 'Visible' : 'Hidden'}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {items.map(item => (
          <div key={item.id} className="relative group">
            <div onClick={() => onImageClick && onImageClick(item)} className="cursor-pointer">
              {item.image ? (
                <img src={item.image} alt={item.title || item.name} className="object-cover w-full h-64 transition-opacity rounded-lg hover:opacity-90" />
              ) : (
                <div className="flex items-center justify-center w-full h-64 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100">
                  <span className="text-4xl">Hero</span>
                </div>
              )}
            </div>
            <div className="mt-3">
              <h4 className="text-lg font-semibold text-gray-900">{item.title || item.name}</h4>
              <p className="text-sm text-gray-600">{item.subtitle || item.description}</p>
              {(item.validFrom || item.validTo) && (
                <p className="mt-1 text-xs text-gray-500">Valid: {item.validFrom} to {item.validTo}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ============================================================================
// CUSTOM MULTI-SELECT DROPDOWN COMPONENT
// ============================================================================
const MultiSelectDropdown = ({ label, options, selectedValues, onChange, placeholder = "— Select —" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const displayText = selectedValues.length === 0
    ? placeholder
    : `${selectedValues.length} selected`;

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block mb-2 text-sm font-medium text-gray-700">{label}</label>
      <div
        className="flex items-center justify-between w-full px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={selectedValues.length === 0 ? "text-gray-400" : "text-gray-900"}>
          {displayText}
        </span>
        <span className="text-gray-400">▼</span>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg max-h-60">
          {options.length > 0 ? (
            options.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => toggleOption(option.value)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))
          ) : (
            <div className="px-4 py-2 text-sm text-gray-400">No options available</div>
          )}
        </div>
      )}
    </div>
  );
};

const toOptionalString = (value) => (
  value === undefined || value === null || value === '' ? '' : String(value)
);

const uniqueOptions = (options) => {
  const seen = new Set();
  return options.filter((option) => {
    if (!option.value || seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
};

const normalizeLocationOption = (item, fallbackLabel, extra = {}) => ({
  value: toOptionalString(item?.id ?? item?.value ?? item?.code ?? item?.name),
  label: item?.name || item?.label || item?.code || fallbackLabel,
  ...extra
});

const getProvinceId = (item) => toOptionalString(
  item?.provinceId ??
  item?.province?.id ??
  item?.provinceCode ??
  item?.province?.code
);

const getDistrictId = (item) => toOptionalString(
  item?.districtId ??
  item?.district?.id ??
  item?.districtCode ??
  item?.district?.code ??
  item?.districtName ??
  item?.district?.name
);

const buildLocationOptions = (result) => {
  const rawCities = Array.isArray(result?.cities) ? result.cities : [];
  const rawDistricts = Array.isArray(result?.districts) ? result.districts : [];
  const rawProvinces = Array.isArray(result?.provinces) ? result.provinces : [];

  const provinces = uniqueOptions(rawProvinces.map((item) => normalizeLocationOption(
    item,
    `Province ${item?.id ?? ''}`.trim()
  )));

  const cities = uniqueOptions(rawCities.map((item) => normalizeLocationOption(
    item,
    `City ${item?.id ?? ''}`.trim(),
    {
      provinceId: getProvinceId(item),
      districtId: getDistrictId(item)
    }
  )));

  const districts = uniqueOptions(rawDistricts.map((item) => {
    const label = item?.name || item?.label || item?.code || `District ${item?.id ?? ''}`.trim();
    return normalizeLocationOption(item, label, {
      provinceId: getProvinceId(item)
    });
  }));

  return {
    provinces,
    districts,
    cities
  };
};

// ============================================================================
// ATTRACTIONS GRID - with checkboxes for bulk selection
// ============================================================================
const AttractionsGrid = ({ onAttractionClick, categoryIds, subCategoryIds, provinceIds, districtIds, cityIds, tagIds, onAddSelected }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  let urlCategoryIds = params.getAll('categoryId') || [];
  if (!urlCategoryIds || urlCategoryIds.length === 0) {
    urlCategoryIds = params.getAll('categoryIds[]') || [];
  }
  if ((!urlCategoryIds || urlCategoryIds.length === 0) && params.get('categoryId')) {
    const raw = params.get('categoryId');
    if (raw && raw.includes(',')) urlCategoryIds = raw.split(',').map(s => s.trim()).filter(Boolean);
  }

  const [attractions, setAttractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const [selectedAttractionMap, setSelectedAttractionMap] = useState({}); // { [id]: attraction }
  const selectedAttractionIds = Object.keys(selectedAttractionMap).map(Number);

  const fetchAttractions = async () => {
    try {
      setLoading(true);
      setError(null);
      const start = (currentPage - 1) * itemsPerPage;

      const paramsObj = {
        start,
        length: itemsPerPage,
      };

      if (categoryIds?.length > 0) {
        paramsObj['categoryIds[]'] = categoryIds;
      } else if (urlCategoryIds?.length > 0) {
        paramsObj['categoryIds[]'] = urlCategoryIds;
      }

      if (subCategoryIds?.length > 0) {
        paramsObj['categorySubCatIds[]'] = subCategoryIds;
      }

      if (provinceIds?.length > 0) {
        paramsObj['provinceIds[]'] = provinceIds;
      }

      if (districtIds?.length > 0) {
        paramsObj['districtIds[]'] = districtIds;
      }

      if (cityIds?.length > 0) {
        paramsObj['cityIds[]'] = cityIds;
      }

      if (tagIds?.length > 0) {
        paramsObj['tagIds[]'] = tagIds;
      }

      const response = await axiosInstance.get(
        '/internal-api/v1/client-web/destination-panel/config/list',
        {
          params: paramsObj,
          paramsSerializer: params => {
            return Object.keys(params)
              .map(key => {
                const val = params[key];
                if (Array.isArray(val)) {
                  return val.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
                }
                return `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
              })
              .join('&');
          }
        }
      );

      if (response.data && response.data.data) {
        const rawItems = response.data.data.filter(item => item.isActive);

        const formatted = rawItems.map(item => ({
          id: item.id,
          uuid: item.uuid,
          name: item.name,
          city: item.cityName || 'Unknown',
          imageUrl: null,
          loadingImage: true
        }));

        setAttractions(formatted);
        setTotalItems(response.data.recordsTotal || 0);

        formatted.forEach(attr => fetchImageForAttraction(attr));
      }
    } catch (err) {
      console.error('Error fetching destinations:', err);
      setError('Failed to load attractions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchImageForAttraction = async (attraction) => {
    if (!attraction.uuid) return;

    try {
      const response = await axiosInstance.get(
        `/internal-api/v1/client-web/destination-panel/config/${attraction.uuid}/view`
      );

      const result = response.data?.result;
      const baseUrl = result?.localImageServer || '';
      let imageUrl = getFirstImageUrl(result?.images, baseUrl);

      if (!imageUrl && Array.isArray(result?.destination?.images)) {
        imageUrl = getFirstImageUrl(result.destination.images);
      }

      if (imageUrl) {
        setAttractions(prev => prev.map(a =>
          a.id === attraction.id ? { ...a, imageUrl, loadingImage: false } : a
        ));
      } else {
        setAttractions(prev => prev.map(a =>
          a.id === attraction.id ? { ...a, loadingImage: false } : a
        ));
      }
    } catch (err) {
      console.error(`Error fetching image for ${attraction.uuid}:`, err);
      setAttractions(prev => prev.map(a =>
        a.id === attraction.id ? { ...a, loadingImage: false } : a
      ));
    }
  };

  useEffect(() => {
    fetchAttractions();
  }, [currentPage, location.search, categoryIds, subCategoryIds, provinceIds, districtIds, cityIds, tagIds]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const toggleSelection = (attraction) => {
  setSelectedAttractionMap(prev => {
    const next = { ...prev };
    if (next[attraction.id]) {
      delete next[attraction.id];
    } else {
      next[attraction.id] = attraction; // store full object, not just id
    }
    return next;
  });
};

  const selectedAttractions = Object.values(selectedAttractionMap);

//   const handleBulkAdd = () => {
//   if (selectedAttractions.length === 0) return;
//   onAttractionClick(null, selectedAttractions);
// };

  return (
    <div className="p-6 bg-white border border-gray-200 shadow-sm rounded-xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">All Destinations</h3>
          <p className="text-sm text-gray-500">Loaded from database</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="bg-gray-100 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600">
            {attractions.length} of {totalItems} Attractions
          </span>
          {selectedAttractionIds.length > 0 && (
          <button
            onClick={() => onAddSelected(selectedAttractions)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors bg-green-600 rounded-lg hover:bg-green-700"
          >
            <CheckSquare size={16} />
            Add Selected {selectedAttractionIds.length} to Carousel
          </button>
          )}
        </div>
      </div>

      {error && <div className="mb-4 text-sm text-center text-red-600">{error}</div>}

      {loading ? (
        <div className="py-12 text-center text-gray-500">Loading attractions...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {attractions.map((attraction) => {
              const isSelected = !!selectedAttractionMap[attraction.id];

              return (
                      <div
                        key={attraction.id}
                        onClick={() => toggleSelection(attraction)}
                        className={`group relative cursor-pointer overflow-hidden rounded-lg border
                          ${isSelected ? 'border-green-500 ring-2 ring-green-300' : 'border-gray-200'}
                          bg-white shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-1`}
                      >
                  <div className="absolute z-20 top-2 right-12">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelection(attraction)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-5 h-5 text-green-600 bg-white border-gray-300 rounded focus:ring-green-500"
                    />
                  </div>

                  <div
                    // onClick={() => onAttractionClick(attraction)}
                    className="cursor-pointer"
                  >
                    <div className="overflow-hidden bg-gray-100 aspect-square">
                      {attraction.loadingImage ? (
                        <div className="w-full h-full bg-gray-200 animate-pulse" />
                      ) : attraction.imageUrl ? (
                        <img src={attraction.imageUrl} alt={attraction.name} className="object-cover w-full h-full" />
                      ) : (
                        <div className="flex items-center justify-center w-full h-full text-xs text-gray-400 bg-gray-200 border-2 border-dashed rounded-lg">
                          No Image
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-semibold text-center text-gray-900 truncate">{attraction.name}</h4>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>
                Previous
              </Button>
              <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
              <Button size="sm" variant="secondary" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// SETTINGS FORM BLOCK - Block Name now required
// ============================================================================
const CarouselSettingsForm = ({ onSet, initialSettings }) => {
  const [blockName, setBlockName] = useState('');
  const [subHeader, setSubHeader] = useState('');
  const [selectedParentCategories, setSelectedParentCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedProvinces, setSelectedProvinces] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedCustomPage, setSelectedCustomPage] = useState('');
  const [itemsPerBlock, setItemsPerBlock] = useState('8');
  const [displayOrder, setDisplayOrder] = useState('1');
  const [validFrom, setValidFrom] = useState('');
  const [validTill, setValidTill] = useState('');


  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [subCategoryToCategoryMap, setSubCategoryToCategoryMap] = useState({});
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [tags, setTags] = useState([]);
  const [customPages, setCustomPages] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const res = await axiosInstance.get('/internal-api/v1/client-web/cms/page/page-blocks/meta');

        if (res.data?.status !== true || !res.data?.result) {
          throw new Error('Invalid meta response');
        }

        let registrationMeta = {};
        try {
          const registrationRes = await axiosInstance.get('/internal-api/v1/client-web/destination-panel/registration/meta');
          registrationMeta = registrationRes.data?.result || {};
        } catch (metaErr) {
          console.warn('Could not load destination location metadata:', metaErr);
        }

        const result = {
          ...registrationMeta,
          ...res.data.result,
          categories: registrationMeta.categories || res.data.result.categories,
          subCategories: res.data.result.subCategories || registrationMeta.subCategories,
          cities: registrationMeta.cities || res.data.result.cities,
          districts: registrationMeta.districts || res.data.result.districts,
          provinces: registrationMeta.provinces || res.data.result.provinces
        };

        if (Array.isArray(result.subCategories)) {
          const mapping = {};
          const categoryOptionMap = new Map();
          const registrationCategoryByName = new Map(
            (registrationMeta.categories || []).map((item) => [
              (item.name || '').trim().toLowerCase(),
              item
            ])
          );

          const subCategoryOptions = result.subCategories.map((item) => {
            const categoryFromName = item?.categoryName
              ? registrationCategoryByName.get(item.categoryName.trim().toLowerCase())
              : null;
            const categoryId = item?.categoryId ?? item?.category?.id ?? categoryFromName?.id;
            const categoryLabel = item?.categoryName || item?.category?.name || categoryFromName?.name || `Category ${categoryId}`;
            const categoryValue = categoryId != null ? String(categoryId) : '';

            if (item?.id != null && categoryValue) {
              mapping[String(item.id)] = categoryValue;
            }

            if (categoryValue && !categoryOptionMap.has(categoryValue)) {
              categoryOptionMap.set(categoryValue, {
                value: categoryValue,
                label: categoryLabel
              });
            }

            return {
              value: String(item.id),
              label: item.name || item.code || `Sub Category ${item.id}`,
              categoryId: categoryValue
            };
          });

          setSubCategoryToCategoryMap(mapping);
          setSubCategories(subCategoryOptions);
          setCategories(Array.from(categoryOptionMap.values()));
        } else if (Array.isArray(result.categories)) {
          setCategories(result.categories.map(item => ({
            value: String(item.id),
            label: item.name || item.code || `Category ${item.id}`
          })));
        }

        const locationOptions = buildLocationOptions(result);
        setProvinces(locationOptions.provinces);
        setDistricts(locationOptions.districts);
        setCities(locationOptions.cities);

        if (Array.isArray(result.destinationTags)) {
          setTags(
            result.destinationTags
              .filter(item => item.code && item.name && item.name.trim() !== '')
              .map(item => ({
                value: String(item.code),
                label: item.name.trim()
              }))
          );
        }

      } catch (err) {
        console.error('Failed to load CMS meta:', err);
        setError('Could not load dropdown options');
      }
    };

    const fetchCustomPages = async () => {
      try {
        const res = await axiosInstance.get('/internal-api/v1/client-web/cms/custom-page/list?start=0&length=100');
        
        if (res.data && Array.isArray(res.data.data)) {
          const pages = res.data.data
            .filter(p => p.isActive)
            .map(p => ({
              value: String(p.id),
              label: p.pageMeta || p.name || `Page ${p.id}`
            }));
          setCustomPages(pages);
        }
      } catch (err) {
        console.error('Failed to load custom pages:', err);
      }
    };

    Promise.all([fetchMeta(), fetchCustomPages()])
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!initialSettings) return;
    setBlockName(initialSettings.blockName || '');
    setSubHeader(initialSettings.subHeader || '');
    setSelectedParentCategories(initialSettings.selectedParentCategories || initialSettings.selectedCategoryIds || []);
    setSelectedCategories(initialSettings.selectedCategories || []);
    setSelectedProvinces(initialSettings.selectedProvinces || []);
    setSelectedDistricts(initialSettings.selectedDistricts || []);
    setSelectedCities(initialSettings.selectedCities || []);
    setSelectedTags(initialSettings.selectedTags || []);
    setSelectedCustomPage(initialSettings.selectedCustomPage || '');
    setItemsPerBlock(String(initialSettings.itemsPerBlock ?? '8'));
    setDisplayOrder(String(initialSettings.displayOrder ?? '1'));
    setValidFrom(initialSettings.validFrom || '');
    setValidTill(initialSettings.validTill || '');
  }, [initialSettings]);

  const isBlockNameValid = blockName.trim().length > 0;
  const canFilterDistrictsByProvince = districts.some(option => option.provinceId);
  const canFilterCitiesByProvince = cities.some(option => option.provinceId);
  const canFilterCitiesByDistrict = cities.some(option => option.districtId);

  const filteredSubCategories = useMemo(() => (
    selectedParentCategories.length > 0
      ? subCategories.filter(option => selectedParentCategories.includes(option.categoryId))
      : []
  ), [selectedParentCategories, subCategories]);

  const filteredDistricts = useMemo(() => (
    selectedProvinces.length > 0 && canFilterDistrictsByProvince
      ? districts.filter(option => selectedProvinces.includes(option.provinceId))
      : districts
  ), [canFilterDistrictsByProvince, districts, selectedProvinces]);

  const filteredCities = useMemo(() => (
    cities.filter((option) => {
      const matchesProvince = selectedProvinces.length === 0 || !canFilterCitiesByProvince || selectedProvinces.includes(option.provinceId);
      const matchesDistrict = selectedDistricts.length === 0 || !canFilterCitiesByDistrict || selectedDistricts.includes(option.districtId);
      return matchesProvince && matchesDistrict;
    })
  ), [canFilterCitiesByDistrict, canFilterCitiesByProvince, cities, selectedDistricts, selectedProvinces]);

  useEffect(() => {
    if (selectedParentCategories.length > 0 && subCategories.length === 0) return;
    setSelectedCategories(prev => prev.filter(value => filteredSubCategories.some(option => option.value === value)));
  }, [filteredSubCategories, selectedParentCategories.length, subCategories.length]);

  useEffect(() => {
    setSelectedDistricts(prev => prev.filter(value => filteredDistricts.some(option => option.value === value)));
  }, [filteredDistricts]);

  useEffect(() => {
    setSelectedCities(prev => prev.filter(value => filteredCities.some(option => option.value === value)));
  }, [filteredCities]);

  const handleSet = () => {
    if (!isBlockNameValid) {
      alert('Please enter a Block Name before setting.');
      return;
    }

    if (!validFrom || !validTill) {
      alert('Please select carousel valid period (Valid From & Valid Till).');
      return;
    }

    if (validTill < validFrom) {
      alert('Valid Till cannot be earlier than Valid From.');
      return;
    }

    onSet({
      blockName,
      subHeader,
      selectedParentCategories,
      selectedCategories,
      selectedCategoryIds: Array.from(new Set(
        [
          ...selectedParentCategories,
          ...selectedCategories
            .map(id => subCategoryToCategoryMap[String(id)])
            .filter(Boolean)
        ]
      )),
      selectedProvinces,
      selectedDistricts,
      selectedCities,
      selectedTags,
      selectedCustomPage,
      itemsPerBlock: Number(itemsPerBlock),
      displayOrder: Number(displayOrder),
      validFrom,
      validTill
    });
  };


  if (loading) {
    return (
      <Card className="p-6 mb-8">
        <h3 className="mb-4 text-xl font-semibold">Carousel Block Settings</h3>
        <div className="py-8 text-center text-gray-500">Loading options from server...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6 mb-8 border-red-200">
        <h3 className="mb-4 text-xl font-semibold">Carousel Block Settings</h3>
        <div className="py-4 text-center text-red-600">{error}</div>
      </Card>
    );
  }

  return (
    <Card className="p-6 mb-8">
      <h3 className="mb-6 text-xl font-semibold text-gray-900">Carousel Block Settings</h3>
      
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Block Name <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            value={blockName}
            onChange={(e) => setBlockName(e.target.value)}
            placeholder="e.g. Featured Summer Destinations"
            className={`w-full px-4 py-2 border ${
              blockName.trim() ? 'border-gray-300' : 'border-red-300 focus:border-red-500'
            } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          {!blockName.trim() && (
            <p className="mt-1 text-xs text-red-600">Block Name is required</p>
          )}
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Sub Header</label>
          <input
            type="text"
            value={subHeader}
            onChange={(e) => setSubHeader(e.target.value)}
            placeholder="e.g. Discover the best places to visit this season"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <MultiSelectDropdown
          label="Category"
          options={categories}
          selectedValues={selectedParentCategories}
          onChange={setSelectedParentCategories}
          placeholder="— All Categories —"
        />

        <MultiSelectDropdown
          label="Sub Category"
          options={filteredSubCategories}
          selectedValues={selectedCategories}
          onChange={setSelectedCategories}
          placeholder={selectedParentCategories.length > 0 ? "— All Sub Categories —" : "— Select Category First —"}
        />

        <MultiSelectDropdown
          label="Province"
          options={provinces}
          selectedValues={selectedProvinces}
          onChange={setSelectedProvinces}
          placeholder="-- All Provinces --"
        />

        <MultiSelectDropdown
          label="District"
          options={filteredDistricts}
          selectedValues={selectedDistricts}
          onChange={setSelectedDistricts}
          placeholder="-- All Districts --"
        />

        <MultiSelectDropdown
          label="City"
          options={filteredCities}
          selectedValues={selectedCities}
          onChange={setSelectedCities}
          placeholder="— All Cities —"
        />

        <MultiSelectDropdown
          label="Tag"
          options={tags}
          selectedValues={selectedTags}
          onChange={setSelectedTags}
          placeholder="— All Tags —"
        />

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Custom Page</label>
          <select
            value={selectedCustomPage}
            onChange={(e) => setSelectedCustomPage(e.target.value)}
            className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— Select Custom Page —</option>
            {customPages.map((page) => (
              <option key={page.value} value={page.value}>
                {page.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Items per Block</label>
          <input
            type="number"
            min="1"
            value={itemsPerBlock}
            onChange={(e) => setItemsPerBlock(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Valid From <span className="text-red-600">*</span>
          </label>
          <input
            type="date"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Valid Till <span className="text-red-600">*</span>
          </label>
          <input
            type="date"
            value={validTill}
            onChange={(e) => setValidTill(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Display Order</label>
          <input
            type="number"
            min="1"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        

      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={handleSet}
          disabled={!isBlockNameValid}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            isBlockNameValid
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Set
        </button>
      </div>
    </Card>
  );
};

// ============================================================================
// CMS LAYOUT
// ============================================================================
const CMSLayout = ({ initialSettings, blockId }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  let categoryIds = params.getAll('categoryId') || [];
  if (!categoryIds || categoryIds.length === 0) {
    categoryIds = params.getAll('categoryIds[]') || [];
  }
  if ((!categoryIds || categoryIds.length === 0) && params.get('categoryId')) {
    const raw = params.get('categoryId');
    if (raw && raw.includes(',')) categoryIds = raw.split(',').map(s => s.trim()).filter(Boolean);
  }
  const blockTitle = params.get('blockTitle') || 'Default Carousel';
  const viewMoreLinkPageIdFromUrl = params.get('viewMorePageId') || '';
  const isEditFromBlockList = Boolean(params.get('blockId'));
  const cameFromBanner = params.get('from') === 'banner';

  const { sections, toggleSectionVisibility, addToCarousel, addToPageBody, updateSection } = useCMS();
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedAttraction, setSelectedAttraction] = useState(null);
  const [selectedAttractionsMulti, setSelectedAttractionsMulti] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const navigate = useNavigate();

  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customItemsPerBlock, setCustomItemsPerBlock] = useState('');
  const [customDisplayOrder, setCustomDisplayOrder] = useState('1');

  const [isSet, setIsSet] = useState(false);
  const [settings, setSettings] = useState({});
  const [carouselId, setCarouselId] = useState('');

  const carouselSection = sections.find(s => s.id === 'carousel-items') || {};
  const carouselItems = carouselSection.items || [];

  const formatDateTime = (dateStr) => dateStr ? `${dateStr} 00:00:00` : '';

  const pageMeta = params.get('pageMeta') || '';

  useEffect(() => {
    if (!initialSettings) return;
    setSettings(initialSettings);
    if (initialSettings.carouselId) {
      setCarouselId(String(initialSettings.carouselId));
    }
    setIsSet(true);
  }, [initialSettings]);

  const handleSet = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
    setIsSet(true);
  };

  const logFormData = (fd, label = "FormData") => {
  console.group(`🟦 ${label}`);

  const obj = {};
    for (const [key, value] of fd.entries()) {
        const displayValue =
        value === ''
            ? '"" (EMPTY STRING)'
            : value === null
            ? 'null'
            : value === undefined
            ? 'undefined'
            : value;

        // collect repeated keys as arrays
        if (obj[key] !== undefined) {
        if (!Array.isArray(obj[key])) obj[key] = [obj[key]];
        obj[key].push(displayValue);
        } else {
        obj[key] = displayValue;
        }

        console.log(`${key}:`, displayValue);
    }

    console.log('🧾 As Object:', obj);
    console.groupEnd();
    };


    const appendArrayOrBlank = (fd, key, arr) => {
    // ✅ if user cleared all selections, send blank to force backend to clear old values
    if (!arr || arr.length === 0) {
        fd.append(key, '');   // user asked to pass without values (' ')
        return;
    }
    arr.forEach((v) => fd.append(key, String(v)));
    };

  const handlePublish = async () => {
    if (!blockId) {
      alert('Missing block id. Unable to update.');
      return;
    }
    if (carouselItems.length === 0) {
      alert('No items in the carousel to publish.');
      return;
    }

    if (!settings.validFrom || !settings.validTill) {
      alert('Please set carousel Valid From and Valid Till in Carousel Settings.');
      return;
    }

    if (!window.confirm(`Publish all ${carouselItems.length} attractions as ONE carousel group?`)) {
      return;
    }

    setPublishing(true);

    const effectiveTitle = settings.blockName || carouselSection.title || pageMeta || blockTitle || 'Selected Carousel Items';
    const effectiveSubHeader = settings.subHeader || '';
    const effectiveMaxItems = settings.itemsPerBlock || carouselSection.itemsPerBlock || carouselItems.length || 0;
    const effectiveDisplayOrder = settings.displayOrder || carouselSection.displayOrder || 1;

    const effectiveViewMorePageId = settings.selectedCustomPage || viewMoreLinkPageIdFromUrl || '';

    const formdata = new FormData();
    if (blockId) {
      formdata.append('id', String(blockId));
    }
    formdata.append('name', effectiveTitle);
    formdata.append('displayOrder', effectiveDisplayOrder.toString());
    formdata.append('viewMoreLinkPageId', effectiveViewMorePageId);
    formdata.append('blockHeader', effectiveTitle);
    formdata.append('subHeader', effectiveSubHeader);
    formdata.append('blockContent', settings.blockContent || '');
    formdata.append('link', settings.link || 'htt');
    formdata.append('isVisible', (settings.isVisible ?? carouselSection.visible) ? '1' : '0');

    appendArrayOrBlank(
    formdata,
    'destinationCarousels[0][categoryIds][]',
    settings.selectedCategories
    );

    appendArrayOrBlank(
    formdata,
    'destinationCarousels[0][provinceIds][]',
    settings.selectedProvinces
    );

    appendArrayOrBlank(
    formdata,
    'destinationCarousels[0][districtIds][]',
    settings.selectedDistricts
    );

    appendArrayOrBlank(
    formdata,
    'destinationCarousels[0][cityIds][]',
    settings.selectedCities
    );

    appendArrayOrBlank(
    formdata,
    'destinationCarousels[0][tagCodes][]',
    settings.selectedTags
    );


    formdata.append('validFrom', formatDateTime(settings.validFrom));
    formdata.append('validTill', formatDateTime(settings.validTill));
    formdata.append('isPublic', (settings.isPublic ?? true) ? '1' : '0');
    if (carouselId) {
      formdata.append('destinationCarousels[0][id]', String(carouselId));
    }
    formdata.append('destinationCarousels[0][maxItems]', effectiveMaxItems.toString());
    formdata.append('destinationCarousels[0][cardAspect]', settings.cardAspect || '1:1');
    formdata.append('destinationCarousels[0][isActionable]', settings.isActionable != null ? String(settings.isActionable) : '0');

    carouselItems.forEach((item, index) => {
      const attraction = item.selectedItem;
      if (!attraction?.uuid) return;
      const displayOrder = item.displayOrder || index + 1;
      if (item.cardId) {
        formdata.append(`destinationCarousels[0][carouselCards][${index}][id]`, String(item.cardId));
      }
    //   formdata.append(`destinationCarousels[0][carouselCards][${index}][isActive]`, item.isActive === false ? '0' : '1');
      
        const resolvedIsActive = item.isActive === false ? 0 : 1;
        // If user removed card or explicitly made inactive -> status 0
        // Otherwise -> status 1 (keep card)
        const resolvedStatus =
        resolvedIsActive === 0 ? 0 : 1;

        formdata.append(
        `destinationCarousels[0][carouselCards][${index}][isActive]`,
        String(resolvedIsActive)
        );

        formdata.append(
        `destinationCarousels[0][carouselCards][${index}][status]`,
        String(resolvedStatus)
        );

      formdata.append(`destinationCarousels[0][carouselCards][${index}][destinationCard][destinationUuid]`, attraction.uuid);
      formdata.append(`destinationCarousels[0][carouselCards][${index}][destinationCard][displayOrder]`, String(displayOrder));
    });

    try {
      logFormData(formdata, "Publish Destination Carousel Payload");
      
      const response = await axiosInstance.post(
        `/internal-api/v1/client-web/cms/page/page-blocks/destination-carousel/${blockId}/edit-group`,
        formdata
      );

      if (response && response.status >= 200 && response.status < 300) {
        alert('Carousel group published successfully as ONE group!');
        
        // Navigate based on whether it's a custom page block or landing page block
        if (pageMeta) {
          // Custom page block - navigate back to CustomPageBlocks
          const navParams = new URLSearchParams();
          const pageIdFromUrl = params.get('pageId');
          if (pageIdFromUrl) navParams.set('pageId', pageIdFromUrl);
          navParams.set('pageName', pageMeta);
          navigate(`/CustomPageBlocks?${navParams.toString()}`);
        } else {
          // Landing page block - navigate to LandingPage
          navigate('/LandingPage');
        }
      } else if (response.data && response.data.result) {
        alert('Carousel group published successfully as ONE group!');
        
        // Navigate based on whether it's a custom page block or landing page block
        if (pageMeta) {
          // Custom page block - navigate back to CustomPageBlocks
          const navParams = new URLSearchParams();
          const pageIdFromUrl = params.get('pageId');
          if (pageIdFromUrl) navParams.set('pageId', pageIdFromUrl);
          navParams.set('pageName', pageMeta);
          navigate(`/CustomPageBlocks?${navParams.toString()}`);
        } else {
          // Landing page block - navigate to LandingPage
          navigate('/LandingPage');
        }
      } else {
        alert('Published, but please verify the result.');
      }
    } catch (error) {
      console.error('Error publishing carousel group:', error);
      alert('Failed to publish carousel group. Check console for details.');
    } finally {
      setPublishing(false);
    }
  };

  const openCustomModal = () => {
    setCustomName(carouselSection.title || pageMeta || blockTitle || 'Custom Carousel');
    setCustomItemsPerBlock(carouselSection.itemsPerBlock || '8');
    setCustomDisplayOrder(carouselSection.displayOrder || '1');
    setCustomModalOpen(true);
  };

  const saveCustomSettings = () => {
    if (!customName.trim()) {
      alert('Please enter a carousel name');
      return;
    }
    if (!customItemsPerBlock || isNaN(Number(customItemsPerBlock)) || Number(customItemsPerBlock) < 1) {
      alert('Please enter a valid number for items per carousel');
      return;
    }

    const updatedSection = {
      ...carouselSection,
      title: customName.trim(),
      itemsPerBlock: Number(customItemsPerBlock),
      displayOrder: Number(customDisplayOrder) || 1,
    };

    updateSection(updatedSection);
    setCustomModalOpen(false);
  };

  const handleAttractionClick = (singleItem, multiItems = null) => {
    if (multiItems) {
      setSelectedAttractionsMulti(multiItems);
      setSelectedAttraction(null);
    } else {
      setSelectedAttraction(singleItem);
      setSelectedAttractionsMulti([]);
    }
    setPopupOpen(true);
  };

  const handleBulkSelectionConfirm = (items) => {
      setSelectedAttractionsMulti(items);
      setPopupOpen(true);
    };


  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4 mx-auto max-w-7xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Attraction Page CMS</h1>
              <p className="mt-1 text-sm text-gray-500">Manage hero, carousel, and page body items</p>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" icon={Eye}>Preview Page</Button>

              {cameFromBanner && (
                <Button
                  variant="primary"
                  onClick={openCustomModal}
                  icon={Edit}
                >
                  Custom
                </Button>
              )}

              <Button
                onClick={handlePublish}
                icon={Save}
                disabled={publishing || carouselItems.length === 0}
                variant="success"
              >
                {publishing ? 'Publishing...' : `Publish as One Group (${carouselItems.length})`}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-8 mx-auto max-w-7xl">
        <div className="mb-6">
          <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
            <p className="text-sm text-blue-800">
              All attractions added to <strong>Carousel</strong> will be published as <strong>ONE single group</strong> when you click the publish button.<br/>
              Validity dates from the Carousel Settings will be used for the group.
            </p>
          </div>
        </div>

        <CarouselSettingsForm onSet={handleSet} initialSettings={settings} />

        {isSet && (
          <div className="space-y-8">
            <ItemsBlockCard
              section={carouselSection}
              titleOverride={`${settings.blockName || pageMeta || blockTitle || 'Selected Carousel Items'} (Will be published as ONE group)`}
              allowEdit={isEditFromBlockList}
              validityLabel={
                settings.validFrom && settings.validTill
                  ? `${settings.validFrom} to ${settings.validTill}`
                  : ''
              }
            />
          </div>
        )}

        {isSet && (
          <div className="mt-10">
            <AttractionsGrid
              // onAttractionClick={handleAttractionClick}
              categoryIds={settings.selectedCategoryIds}
              subCategoryIds={settings.selectedCategories}
              provinceIds={settings.selectedProvinces}
              districtIds={settings.selectedDistricts}
              cityIds={settings.selectedCities}
              tagIds={settings.selectedTags}
              onAddSelected={(items) => {
                setSelectedAttractionsMulti(items);
                setPopupOpen(true);
              }}
            />
          </div>
        )}
      </div>

      <div className="px-6 pb-8 mx-auto max-w-7xl">
        <Button onClick={() => navigate('/CMSDashboard')} variant="secondary" icon={ArrowLeft} className="w-full">
          Back to CMS Dashboard
        </Button>
      </div>

      {popupOpen && (
        <DestinationsPopup
          selectedItems={selectedAttractionsMulti}
          isMulti={true}
          section={{}} // validity now comes from carousel settings
          onClose={() => {
            setPopupOpen(false);
            setSelectedAttractionsMulti([]);
          }}
          onAddToCarousel={addToCarousel}
        />
      )}

      {customModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60">
          <div className="w-full max-w-md bg-white shadow-2xl rounded-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-xl font-bold text-gray-800">Custom Carousel Settings</h2>
              <button 
                onClick={() => setCustomModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Carousel Name</label>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Featured Summer Destinations"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Items per Carousel</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={customItemsPerBlock}
                  onChange={(e) => setCustomItemsPerBlock(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 8"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Display Order</label>
                <input
                  type="number"
                  min="1"
                  value={customDisplayOrder}
                  onChange={(e) => setCustomDisplayOrder(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 1"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setCustomModalOpen(false)}
                className="px-5 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={saveCustomSettings}
                className="px-5 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const toDateInput = (value) => {
  if (!value) return '';
  if (value.includes('T')) return value.split('T')[0];
  if (value.includes(' ')) return value.split(' ')[0];
  return value;
};

const AttractionEdit = () => {
  const [sections, setSections] = useState(initialSections);
  const [initialSettings, setInitialSettings] = useState(null);
  const [blockId, setBlockId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();
const urlParams = new URLSearchParams(location.search);

const validFromFromUrl = urlParams.get('validFrom');
const validTillFromUrl = urlParams.get('validTill');


  useEffect(() => {
    const fetchBlock = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const blockIdParam = params.get('blockId');

        if (!blockIdParam) {
          setError('Missing blockId in URL');
          setLoading(false);
          return;
        }

        setBlockId(String(blockIdParam));
        setLoading(true);
        setError(null);

        const res = await axiosInstance.get(
          `/internal-api/v1/client-web/cms/page/page-blocks/${blockIdParam}/view`
        );

        const result = res.data?.result;
        const data = result?.data;
        const carousel = data?.carousels?.[0] || {};

        const title = data?.title || 'Destination Carousel';
        const subHeader = data?.subTitle || '';
        const isVisible = !!data?.isVisible;
        const isPublic = data?.isPublic ?? true;
        const blockContent = data?.content || '';
        const link = data?.viewMoreLink || 'htt';
        const displayOrder = Number(result?.displayOrder || 1);
        const maxItems = Number(carousel?.maxItems || 8);
        const cardAspect = carousel?.cardAspect || '1:1';
        const isActionable = carousel?.isActionable ?? 0;
        const carouselId = carousel?.id ? String(carousel.id) : '';

        const categorySybCategories = Array.isArray(carousel?.categorySybCategories)
          ? carousel.categorySybCategories
          : [];

        const selectedCategories = categorySybCategories
          .map((x) => x?.categorySubCategoryId ?? x?.subCategory?.id)
          .filter((v) => v != null)
          .map((v) => String(v));

        const selectedCategoryIds = Array.from(
          new Set(
            categorySybCategories
              .map((x) => x?.category?.id)
              .filter((v) => v != null)
              .map((v) => String(v))
          )
        );

        const selectedCities = Array.isArray(carousel?.cities)
          ? carousel.cities
              .map((x) => x?.cityId)
              .filter((v) => v != null)
              .map((v) => String(v))
          : [];

        const selectedProvinces = Array.isArray(carousel?.provinces)
          ? carousel.provinces
              .map((x) => x?.provinceId ?? x?.id)
              .filter((v) => v != null)
              .map((v) => String(v))
          : [];

        const selectedDistricts = Array.isArray(carousel?.districts)
          ? carousel.districts
              .map((x) => x?.districtId ?? x?.id)
              .filter((v) => v != null)
              .map((v) => String(v))
          : [];

        const selectedTags = Array.isArray(carousel?.blockTags)
          ? carousel.blockTags
              .map((x) => x?.tagCode)
              .filter(Boolean)
              .map((v) => String(v))
          : [];

        const cards = Array.isArray(carousel?.cards) ? carousel.cards : [];
        const mappedItems = cards.map((c, index) => {
          const d = c?.destinationCard?.destination || {};
          const name = (d?.displayName || d?.name || '').trim() || '-';
          const imageUrl = getFirstImageUrl(d?.images);
          return {
            id: c?.id ?? Math.random(),
            cardId: c?.id || '',
            isActive: c?.isActive ?? true,
            status: c?.status ?? 1,
            displayOrder: c?.displayOrder ?? index + 1,
            selectedItem: {
              id: d?.id,
              uuid: d?.uuid,
              name,
              city: d?.cityName || 'Unknown',
              imageUrl,
            }
          };
        });

        setSections(prev => prev.map(s => {
          if (s.id !== 'carousel-items') return s;
          return {
            ...s,
            title,
            itemsPerBlock: maxItems,
            visible: isVisible,
            items: mappedItems
          };
        }));

        setInitialSettings({
          blockName: title,
          subHeader,
          selectedParentCategories: selectedCategoryIds,
          selectedCategories,
          selectedCategoryIds,
          selectedProvinces,
          selectedDistricts,
          selectedCities,
          selectedTags,
          selectedCustomPage: data?.viewMoreLinkPageId ? String(data.viewMoreLinkPageId) : '',
          itemsPerBlock: maxItems,
          displayOrder,
          validFrom: toDateInput(data?.validFrom) || toDateInput(validFromFromUrl),
          validTill: toDateInput(data?.validTill) || toDateInput(validTillFromUrl),
          isVisible,
          isPublic,
          blockContent,
          link,
          cardAspect,
          isActionable,
          carouselId
        });
      } catch (err) {
        console.error('Failed to load edit block:', err);
        setError('Failed to load carousel block.');
      } finally {
        setLoading(false);
      }
    };

    fetchBlock();
  }, [location.search]);

  const updateSection = (updatedSection) => {
    setSections(sections.map(s => s.id === updatedSection.id ? updatedSection : s));
  };

  const toggleSectionVisibility = (sectionId) => {
    setSections(sections.map(s => s.id === sectionId ? { ...s, visible: !s.visible } : s));
  };

  const addToCarousel = (newItems) => {
    const items = Array.isArray(newItems) ? newItems : [newItems];
    setSections(sections.map(section =>
      section.id === 'carousel-items'
        ? {
            ...section,
            items: [
              ...section.items,
              ...items.map((i, idx) => ({
                ...i,
                id: Date.now() + Math.random(),
                cardId: '',
                isActive: true,
                status: 0,
                displayOrder: section.items.length + idx + 1
              }))
            ]
          }
        : section
    ));
  };

  const addToPageBody = (newItems) => {
    const items = Array.isArray(newItems) ? newItems : [newItems];
    setSections(sections.map(section =>
      section.id === 'page-body-items'
        ? { ...section, items: [...section.items, ...items.map(i => ({ ...i, id: Date.now() + Math.random() }))] }
        : section
    ));
  };

  const removeFromBlock = (sectionId, itemId) => {
    setSections((prevSections) =>
        prevSections.map((section) => {
        if (section.id !== sectionId) return section;

        return {
            ...section,
            items: section.items
            .map((it) => {
                if (it.id !== itemId) return it;

                // ✅ If this card exists in DB, soft-delete it (send isActive=0 on publish)
                if (it.cardId) {
                return { ...it, isActive: false };
                }

                // ✅ If newly added (no cardId), remove completely
                return null;
            })
            .filter(Boolean),
        };
        })
    );
    };

  const contextValue = {
    sections,
    updateSection,
    toggleSectionVisibility,
    addToCarousel,
    addToPageBody,
    removeFromBlock
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-gray-500">Loading carousel block...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <CMSContext.Provider value={contextValue}>
      <CMSLayout initialSettings={initialSettings} blockId={blockId} />
    </CMSContext.Provider>
  );
};

export default AttractionEdit;
